import React, { useState } from 'react'
import Select from 'react-select'
import QuestionChildOptionPills from './QuestionChildOptionPills'
import { buildQuestionOptionList, getActiveQuestionOptions, selectQuestionParentOptions, toggleQuestionChildOption } from './questionOptionHierarchy'
import { CButton, CCard, CCardBody, CCardFooter, CCardHeader, CCardTitle, CFormLabel, CFormSelect, CModal, CModalBody, CModalFooter, CTooltip, CModalHeader, CModalTitle, CHeader } from '@coreui/react'
import { CircleAlertIcon, Pencil, Trash2, ListOrdered, Info } from 'lucide-react'

const DisplayQuestion = ({ fetchedQuestions, handleDragStart, handleDragOver, handleDrop, handleDeleteQuestion, handleEditQuestion, setDeleteVisible, setDeleteQuestionIndex }) => {
  const [inputValues, setInputValues] = useState({})
  const [optionSelections, setOptionSelections] = useState({})

  const handleInputChange = (e, id) => {
    setInputValues((prev) => ({
      ...prev,
      [id]: e.target.value,
    }))
  }

  const updateSelection = (key, question, makePatch) => {
    setOptionSelections(previous => ({
      ...previous,
      [key]: makePatch({ ...question, ...previous[key] }),
    }))
  }

  const renderQuestions = (questions, isNested = false, parentPath = []) => {
    return questions.map((ques, idx) => {
      const questionPath = [...parentPath, ques.id != null ? `question:${ques.id}` : `index:${idx}`]
      const questionKey = JSON.stringify(questionPath)
      const question = { ...ques, ...optionSelections[questionKey] }
      const choices = buildQuestionOptionList(ques.options)
      const parentChoices = choices.filter(choice => choice.depth === 0)
      const activeChoices = getActiveQuestionOptions(question, choices)
      const selectedParents = activeChoices.filter(choice => choice.depth === 0)
      return (
        <div
          key={questionKey}
          draggable={!isNested}
          onDragStart={(e) => !isNested && handleDragStart(e, idx)}
          onDrop={(e) => !isNested && handleDrop(e, idx)}
          onDragOver={!isNested && handleDragOver}
          className={`custom-form-builder-theme__question-card d-flex flex-column gap-3 p-3 mb-3 shadow-sm ${isNested ? 'custom-form-builder-theme__question-card--nested' : ''} ${ques?.type === 'input' ? 'visible' : ques?.type === 'dropdown' ? 'visible' : ques?.type === 'Header' || ques?.type === 'subHeader' ? 'visible' : 'invisible d-none'}`}
          style={{
            cursor: 'move',
            height: 'auto',
            borderRadius: isNested ? '5px' : '20px',
            maxWidth: '100%',
            paddingLeft: isNested ? '16px' : '0',
            margin: isNested ? '12px' : '0',
          }}
        >
          {ques?.type == 'Header' ? (
            //  HEADER
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <label className={`${isNested ? 'fw-medium' : 'fw-bold'}`} style={{ maxWidth: '400px' }}>
                    {ques?.label}
                  </label>
                </div>
                <span className="d-flex align-items-center gap-2">
                    <button onClick={() => handleEditQuestion(ques, idx, true)} className="btn btn-sm btn-outline-warning custom-form-builder-theme__icon-btn custom-form-builder-theme__icon-btn--warning" size="sm">
                      <Pencil size={20} strokeWidth={2.25} />
                    </button>
                  <button
                    className="btn btn-sm btn-outline-danger custom-form-builder-theme__icon-btn custom-form-builder-theme__icon-btn--danger"
                    onClick={() => {
                      setDeleteVisible(true)
                      setDeleteQuestionIndex(idx)
                    }}
                  >
                    <Trash2 size={20} strokeWidth={1.75} />
                  </button>
                </span>
              </div>
            </div>
          ) : ques?.type === 'subHeader' ? (
            //Sub Header
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <label className={`${isNested ? 'fw-medium' : 'fw-bold  '}`} style={{ maxWidth: '400px' }}>
                    {ques?.label}
                  </label>
                </div>
                <span className="d-flex align-items-center gap-2">
                    <button onClick={() => handleEditQuestion(ques, idx, true)} className="btn btn-sm btn-outline-warning custom-form-builder-theme__icon-btn custom-form-builder-theme__icon-btn--warning" size="sm">
                      <Pencil size={20} strokeWidth={2.25} />
                    </button>
                  <button
                    className="btn btn-sm btn-outline-danger custom-form-builder-theme__icon-btn custom-form-builder-theme__icon-btn--danger"
                    onClick={() => {
                      setDeleteVisible(true)
                      setDeleteQuestionIndex(idx)
                    }}
                  >
                    <Trash2 size={20} strokeWidth={1.75} />
                  </button>
                </span>
              </div>
            </div>
          ) : ques?.type === 'input' ? (
            // INPUT
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <label className={`${isNested ? 'fw-medium' : 'fw-bold'}`} style={{ maxWidth: '400px' }}>
                    {ques?.label}
                  </label>
                  <CTooltip content={ques?.toolTipValue ? ques?.toolTipValue : 'Add the tooltip'}>
                    <Info size={16} />
                  </CTooltip>
                </div>
                {!isNested && (
                  <span className="d-flex align-items-center gap-2">
                    <button onClick={() => handleEditQuestion(ques, idx)} className="btn btn-sm btn-outline-warning custom-form-builder-theme__icon-btn custom-form-builder-theme__icon-btn--warning" size="sm">
                      <Pencil size={20} strokeWidth={2.25} />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger custom-form-builder-theme__icon-btn custom-form-builder-theme__icon-btn--danger"
                      onClick={() => {
                        setDeleteVisible(true)
                        setDeleteQuestionIndex(idx)
                      }}
                    >
                      <Trash2 size={20} strokeWidth={1.75} />
                    </button>
                  </span>
                )}
              </div>

              {ques?.isTextArea ? (
                <textarea value={inputValues[questionKey] || ''} onChange={(e) => handleInputChange(e, questionKey)} placeholder="Enter your answer here..." className="form-control" rows="4" />
              ) : (
                <input type="text" onChange={(e) => handleInputChange(e, questionKey)} value={inputValues[questionKey] || ''} placeholder="Enter your answer here..." className="form-control" />
              )}
            </div>
          ) : (
            // DROPDOWN
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <label className={`${isNested ? 'fw-medium' : 'fw-bold'}`} style={{ maxWidth: '400px' }}>
                    {ques?.label}
                  </label>
                  <CTooltip content={ques?.toolTipValue ? ques?.toolTipValue : 'Add the tooltip'}>
                    <Info size={16} />  
                  </CTooltip>
                </div>
                {!isNested && (
                  <div className="d-flex align-items-center gap-2">
                    <button onClick={() => handleEditQuestion(ques, idx)} className="btn btn-sm btn-outline-warning custom-form-builder-theme__icon-btn custom-form-builder-theme__icon-btn--warning">
                      <Pencil size={20} strokeWidth={2.25} />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger custom-form-builder-theme__icon-btn custom-form-builder-theme__icon-btn--danger"
                      onClick={() => {
                        setDeleteVisible(true)
                        setDeleteQuestionIndex(idx)
                      }}
                    >
                      <Trash2 size={20} strokeWidth={1.75} />
                    </button>
                  </div>
                )}
              </div>

              <Select
                menuPosition="fixed"
                isMulti={ques?.isMultiple}
                options={parentChoices}
                getOptionValue={(option) => option.selectionKey}
                value={ques.isMultiple ? selectedParents : selectedParents[0] || null}
                isClearable
                onChange={(selected) => updateSelection(questionKey, ques, current => selectQuestionParentOptions(current, selected))}
                className="basic-multi-select"
                classNamePrefix="select"
                styles={{
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                }}
              />

              <QuestionChildOptionPills
                question={question}
                onToggle={(selectionKey) => updateSelection(questionKey, ques, current => toggleQuestionChildOption(current, selectionKey))}
              />

              {activeChoices.map(choice => {
                const linked = choice.option.linkedQuestion
                const linkedQuestions = Array.isArray(linked) ? linked : linked ? [linked] : []
                return linkedQuestions.length > 0 && (
                  <React.Fragment key={choice.selectionKey}>
                    {renderQuestions(linkedQuestions, true, [...questionPath, choice.selectionKey])}
                  </React.Fragment>
                )
              })}
            </div>
          )}
        </div>
      )
    })
  }

  return <div>{fetchedQuestions.questionData && renderQuestions(fetchedQuestions.questionData)}</div>
}

export default DisplayQuestion
